import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import ReferenceList from '../../components/health/ReferenceList';
import sugarData from '../../data/health/sugar';

function formatCitations(text) {
  return text.replace(
    /\[(\d+)\]/g,
    '<a href="#ref-$1" class="health-cite">[$1]</a>'
  ).replace(
    /\[VERIFY\]/g,
    '<span class="health-verify">[VERIFY]</span>'
  );
}

const AnimatedSection = ({ children, className }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.45 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const HealthSugar = () => {
  const { forms, bodyEffects, brainAndMood, byAgeBracket, practical, references } = sugarData;

  return (
    <>
      <Helmet>
        <title>Sugar Deep Dive | Health</title>
        <meta name="description" content="A comprehensive look at sugar — all forms, how it affects every system in your body, brain and mood impacts, and practical steps to reduce intake." />
        <meta property="og:title" content="Sugar: The Full Picture" />
        <meta property="og:description" content="All forms of sugar, what it does to your body and brain, age-specific impacts, and practical reduction strategies." />
      </Helmet>

      <div className="health-page">
        <h1 className="health-page-title">{sugarData.title}</h1>
        <p className="text-gray-300" style={{ maxWidth: 640, marginBottom: '2.5rem' }}>
          {sugarData.subtitle}
        </p>

        {/* All forms of sugar */}
        <AnimatedSection className="health-content">
          <h2>{forms.title}</h2>
          <p dangerouslySetInnerHTML={{ __html: formatCitations(forms.intro) }} />

          {forms.items.map((item, i) => (
            <div key={i} className="health-callout" style={{ marginBottom: '0.75rem' }}>
              <div className="health-callout-title">{item.name}</div>
              <div
                className="health-callout-text"
                dangerouslySetInnerHTML={{ __html: formatCitations(item.detail) }}
              />
            </div>
          ))}

          <p style={{ marginTop: '1rem' }}>
            <strong>{forms.takeaway}</strong>
          </p>
        </AnimatedSection>

        <hr className="health-divider" />

        {/* Body effects by system */}
        <AnimatedSection className="health-content">
          <h2>{bodyEffects.title}</h2>

          {bodyEffects.systems.map((system) => (
            <div key={system.name} style={{ marginBottom: '2rem' }}>
              <h3>
                <span style={{ marginRight: '0.5rem' }}>{system.icon}</span>
                {system.name}
              </h3>
              <ul>
                {system.points.map((point, j) => (
                  <li
                    key={j}
                    dangerouslySetInnerHTML={{ __html: formatCitations(point) }}
                  />
                ))}
              </ul>
            </div>
          ))}
        </AnimatedSection>

        <hr className="health-divider" />

        {/* Brain and mood */}
        <AnimatedSection className="health-content">
          <h2>{brainAndMood.title}</h2>

          {brainAndMood.sections.map((section, i) => (
            <div key={i} style={{ marginBottom: '1.5rem' }}>
              <h3>{section.heading}</h3>
              <p dangerouslySetInnerHTML={{ __html: formatCitations(section.text) }} />
            </div>
          ))}
        </AnimatedSection>

        <hr className="health-divider" />

        {/* Sugar by age bracket */}
        <AnimatedSection className="health-content">
          <h2>{byAgeBracket.title}</h2>

          {byAgeBracket.brackets.map((bracket) => (
            <div key={bracket.age} className="health-callout" style={{ marginBottom: '0.75rem' }}>
              <div className="health-callout-title">
                Ages {bracket.age}
                <Link
                  to={`/health/by-age/${bracket.slug}`}
                  style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--color-accent)' }}
                >
                  Full guide &rarr;
                </Link>
              </div>
              <div
                className="health-callout-text"
                dangerouslySetInnerHTML={{ __html: formatCitations(bracket.text) }}
              />
            </div>
          ))}
        </AnimatedSection>

        <hr className="health-divider" />

        {/* Practical */}
        <AnimatedSection className="health-content">
          <h2>{practical.title}</h2>

          <h3>{practical.labelReading.heading}</h3>
          <ul>
            {practical.labelReading.points.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>

          <h3>{practical.limits.heading}</h3>
          <ul>
            {practical.limits.points.map((point, i) => (
              <li
                key={i}
                dangerouslySetInnerHTML={{ __html: formatCitations(point) }}
              />
            ))}
          </ul>

          <h3>{practical.swaps.heading}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="health-table">
              <thead>
                <tr>
                  <th className="dont-col">Instead of</th>
                  <th className="do-col">Try</th>
                </tr>
              </thead>
              <tbody>
                {practical.swaps.items.map((swap, i) => (
                  <tr key={i}>
                    <td>{swap.from}</td>
                    <td>{swap.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnimatedSection>

        {/* References */}
        <div className="health-content">
          <ReferenceList references={references} />
        </div>
      </div>
    </>
  );
};

export default HealthSugar;
